import { inject, Injectable, runInInjectionContext } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AuthenticationResult, InteractionStatus } from '@azure/msal-browser';
import {
  tap,
  switchMap,
  of,
  map,
  from,
  catchError,
  filter,
  Observable,
  take,
  EMPTY,
  throwError,
  defer,
  timer,
} from 'rxjs';
import { Implementer } from '../models/implementer';
import { User } from '../models/user';
import { UserService } from './api/user.service';
import { Router } from '@angular/router';
import { ImplementerRegistrationFacadeService } from './implementer-registration-facade.service';
import { ImplementerRegistrationFlowService } from './implementer-registration-flow.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { refreshable } from '../shared/resources/refreshable';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class UserFacadeService {
  private msalService = inject(MsalService);
  private msalBroadcastService = inject(MsalBroadcastService);
  private userService = inject(UserService);
  private router = inject(Router);
  private implementerFacadeService = inject(ImplementerRegistrationFacadeService);
  private implementerRegistrationFlowService = inject(ImplementerRegistrationFlowService);

  private currentUserWithImplementer$ = defer(() => {
    const activeAccount = this.msalService.instance.getActiveAccount();

    if (!activeAccount) {
      // GC - This is silly, but without the timer, the message is logged twice. It appears Angular subscribes to the Signal
      // and then immediately unsubscribes. The timer makes it run asynchronously, so the first subscription doesn't get as
      // far as logging the message.
      return timer(0).pipe(
        tap(() =>
          console.debug(
            '[UserFacadeService] currentUserWithImplementer$: active account is not set.',
          ),
        ),
        map(() => ({
          user: null,
          implementer: null,
        })),
      );
    }

    return this.userService.getCurrentUser().pipe(
      tap(user =>
        console.debug('[UserFacadeService] currentUserWithImplementer$: user?.email=', user?.email),
      ),
      switchMap(user => {
        if (!user) {
          return of({
            user: null,
            implementer: null,
          });
        }

        return this.userService.getImplementerForCurrentUser().pipe(
          map(implementer => ({
            user: user,
            implementer: implementer,
          })),
          tap(userWithImplementer =>
            console.debug(
              '[UserFacadeService] currentUserWithImplementer$: userWithImplementer.implementer?.legalName=',
              userWithImplementer.implementer?.legalName,
            ),
          ),
        );
      }),
    );
  });

  currentUserImplementer = refreshable(() => this.currentUserWithImplementer$);

  constructor() {
    this.implementerFacadeService.loadImplementer$.pipe(takeUntilDestroyed()).subscribe();
  }

  logInOrCreateUser(prompt: 'login' | 'create') {
    const activeAccount = this.msalService.instance.getActiveAccount();
    console.debug(
      '[UserFacadeService] logInOrCreateUser: active account username:',
      activeAccount?.username,
    );

    const isLoggedIn = activeAccount !== null;
    const resolvedPrompt = isLoggedIn ? 'select_account' : prompt;

    return this.msalService
      .loginPopup({
        scopes: environment.scopes,
        prompt: resolvedPrompt,
      })
      .pipe(
        tap(result => this.handleAuthenticationResult(result)),
        switchMap(result => this.extractUserAndRoles(result)),
        switchMap(userWithRoles => this.fetchImplementer(userWithRoles)),
        switchMap(response => this.routeUser(response, prompt)),
        catchError(err => {
          if (err?.errorCode === 'user_cancelled') {
            console.info('[UserFacadeService] logInOrCreateUser: login popup cancelled by user.');
            return EMPTY;
          }

          // Re-throw other errors
          return throwError(() => err);
        }),
      );
  }

  private handleAuthenticationResult(result: AuthenticationResult): void {
    console.debug(
      '[UserFacadeService] handleAuthenticationResult: setting active account to account with username: ',
      result.account.username,
    );
    this.msalService.instance.setActiveAccount(result.account);
    this.currentUserUpdated();

    const loginHint = result.account.idTokenClaims?.login_hint;
    if (loginHint) {
      localStorage.setItem('loginHint', loginHint);
    }
  }

  private extractUserAndRoles(result: AuthenticationResult) {
    const idTokenClaims = result.idTokenClaims as any;
    const roles: string[] = idTokenClaims?.roles || [];
    return this.userService.getCurrentUser().pipe(map(user => ({ user, roles })));
  }

  private fetchImplementer(userWithRoles: { user: User | null; roles: string[] }) {
    if (!userWithRoles.user) {
      return of({ userWithRoles, implementer: null });
    }
    return this.userService
      .getImplementerForCurrentUser()
      .pipe(map(implementer => ({ userWithRoles, implementer })));
  }

  private routeUser(
    response: {
      userWithRoles: { user: User | null; roles: string[] };
      implementer: Implementer | null;
    },
    prompt: 'login' | 'create',
  ) {
    const { user, roles } = response.userWithRoles;
    const isAdmin = roles.includes('Vanhu.Admin');

    if (isAdmin) {
      console.debug(
        '[UserFacadeService] routeUser: user is an admin, navigating to /implementers.',
      );
      return from(this.router.navigate(['/implementers']));
    }

    if (!user) {
      if (prompt === 'create') {
        console.debug(
          '[UserFacadeService] routeUser: no user profile found and user clicked Sign Up; navigating to /create-user.',
        );
        return from(this.router.navigate(['/create-user']));
      } else {
        console.debug(
          '[UserFacadeService] routeUser: no user profile found and user clicked Log In; navigating to /sign-up.',
        );
        return from(this.router.navigate(['/sign-up']));
      }
    }

    if (!response.implementer) {
      console.debug(
        '[UserFacadeService] routeUser: no implementer found, navigating to /create-implementer.',
      );
      return from(this.router.navigate(['/create-implementer']));
    }

    const implementer = response.implementer;
    this.implementerFacadeService.setImplementerId(implementer.id);

    const isImplementerAdmin = implementer.users.some(u => u.user.id === user.id && u.isAdmin);

    if (isImplementerAdmin) {
      console.debug(
        '[UserFacadeService] routeUser: user is an implementer admin, navigating to current step.',
      );
      return this.implementerRegistrationFlowService.navigateToCurrentStep(implementer.id);
    } else {
      console.debug(
        '[UserFacadeService] routeUser: user is not an implementer admin, navigating to /already-registered.',
      );
      return from(this.router.navigate(['/implementer', implementer.id, 'already-registered']));
    }
  }

  addCurrentUser(user: User) {
    return this.userService.addCurrentUser(user).pipe(tap(() => this.currentUserUpdated()));
  }

  updateCurrentUser(user: User) {
    return this.userService.updateCurrentUser(user).pipe(tap(() => this.currentUserUpdated()));
  }

  currentUserUpdated() {
    this.currentUserImplementer.refresh();
  }

  currentUserImplementerUpdated() {
    this.currentUserImplementer.refresh();
  }

  logOut(): Observable<boolean> {
    const activeAccount = this.msalService.instance.getActiveAccount();

    if (!activeAccount) {
      return of(true);
    }

    const logoutHint = localStorage.getItem('loginHint');

    return this.msalBroadcastService.inProgress$.pipe(
      filter(status => status === InteractionStatus.None || status === InteractionStatus.Startup),
      take(1),
      switchMap(() =>
        this.msalService
          .logout({
            account: activeAccount,
            logoutHint: logoutHint ?? undefined,
          })
          .pipe(
            map(() => this.msalService.instance.getActiveAccount() === null),
            catchError(err => {
              console.error('Logout failed:', err);
              return of(false);
            }),
          ),
      ),
    );
  }
}
